import { useEffect } from "react";
import { API } from "../services/api-services";
import { auth, getUser } from "../utils/auth";
import { decrypt } from "../utils/other";
import { getCookie, setCookie } from "../utils/cookieUtils";

// When the guard decides an unpaid student must leave the portal, it no longer
// redirects to Sandbox silently. It raises this signal so the non-closable
// RedirectToSandboxModal shows — giving a 10s countdown, a "Redirect now"
// button, and a "Logout" option (so the user is never bounced to Sandbox
// without a chance to log out of the portal). Shown once per page load.
export const SANDBOX_REDIRECT_EVENT = "cy_show_sandbox_redirect";
let sandboxRedirectPending = false;
export const isSandboxRedirectPending = () => sandboxRedirectPending;
const triggerSandboxRedirectPopup = () => {
  if (sandboxRedirectPending) return;
  sandboxRedirectPending = true;
  window.dispatchEvent(new Event(SANDBOX_REDIRECT_EVENT));
};

// Flags / cookie / event shared between this guard and the layout (useProtectedRoute).
//
// _cy_from_sandbox (sessionStorage) : set by the auth bridge when the user crossed
//     over from Sandbox — marks the whole tab session.
// _cy_is_paid (cookie)              : the current user's id, written once the backend
//     confirms the student is paid. Bound to the user id (not just "true") so a stale
//     cookie can never grant access to a different user on the same browser; expires in
//     1 month so paid status is re-verified periodically.
// cy_sidebar_visibility_update      : window event fired after the cookie is written so
//     the already-mounted layout re-renders and reveals the sidebar without a reload.
export const SANDBOX_FLAG = "_cy_from_sandbox";
export const IS_PAID_COOKIE = "_cy_is_paid";
export const SIDEBAR_EVENT = "cy_sidebar_visibility_update";

const IS_PAID_TTL_SECONDS = 30 * 24 * 60 * 60; // 1 month

// Per page-load memo of the resolved paid status, plus a shared in-flight promise so
// concurrent evaluations (mount + hashchange + StrictMode) dedupe to one API call.
let cached; // true | false | undefined (unknown)
let checkPromise = null;

const currentUserId = () => getUser()?.studentId;

/**
 * Synchronous read of the cached paid status — true only when the cookie matches the
 * current user's id. Safe for the layout to call on every render.
 */
export const isPaidCached = () => {
  const uid = currentUserId();
  return !!uid && getCookie(IS_PAID_COOKIE) === uid;
};

const currentPath = () => {
  const h = window.location.hash || "";
  const noHash = h.startsWith("#") ? h.slice(1) : h;
  return noHash.split("?")[0] || "/";
};

const isDashboardRoute = (p) =>
  p === "/dashboard" ||
  p.startsWith("/dashboard/") ||
  p === "/sb" ||
  p.startsWith("/sb/");

const isParentUser = () => {
  try {
    return !!decrypt(localStorage.getItem("user"))?.parentId;
  } catch {
    return false;
  }
};

// Only students inside the web portal are subject to the paid gate.
const guardable = () =>
  auth() && !window.ReactNativeWebView && !isParentUser();

/**
 * Resolves the paid status for this page load: cookie first, then the
 * /student/is-paid API. On a paid result the cookie is written and SIDEBAR_EVENT
 * fired. On error the status is left unknown (no redirect — the safe default).
 */
const ensurePaidKnown = () => {
  if (cached !== undefined) return Promise.resolve(cached);
  if (isPaidCached()) {
    cached = true;
    return Promise.resolve(true);
  }
  if (!checkPromise) {
    checkPromise = API.get("/student/is-paid")
      .then((res) => {
        cached = !!res?.data?.isPaid;
        if (cached) {
          const uid = currentUserId();
          if (uid) {
            setCookie(IS_PAID_COOKIE, uid, { maxAge: IS_PAID_TTL_SECONDS });
            window.dispatchEvent(new Event(SIDEBAR_EVENT));
          }
        }
        return cached;
      })
      .catch(() => {
        // Unknown — don't eject paid users on a transient failure. Allow a retry.
        checkPromise = null;
        return undefined;
      });
  }
  return checkPromise;
};

/**
 * App-root guard for paid access. For students on the dashboard (or anyone arriving
 * from Sandbox) it resolves paid status and, when the user is NOT paid and sitting on
 * the dashboard, redirects them back to the standalone Sandbox. Paid users get their
 * sidebar revealed via the cookie + SIDEBAR_EVENT. No-op for everyone else.
 */
export default function usePaidAccessGuard() {
  useEffect(() => {
    const evaluate = () => {
      if (!guardable()) return;

      const onDash = isDashboardRoute(currentPath());
      const fromSandbox = sessionStorage.getItem(SANDBOX_FLAG) === "1";
      // Nothing to decide for a normal user away from the dashboard.
      if (!onDash && !fromSandbox) return;

      ensurePaidKnown().then((paid) => {
        if (paid === false && isDashboardRoute(currentPath()) && guardable()) {
          // Show the non-closable redirect popup (10s countdown + Logout option)
          // instead of bouncing straight to Sandbox.
          triggerSandboxRedirectPopup();
        }
      });
    };

    evaluate();
    window.addEventListener("hashchange", evaluate);
    return () => window.removeEventListener("hashchange", evaluate);
  }, []);
}
